import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import HomePage from './pages/HomePage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import PropertiesPage from './pages/PropertiesPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import PropertyEditorPage from './pages/PropertyEditorPage';
import { observeAuthState } from './services/authService';
import fixLeafletIcon from './utils/leafletFix';

// Protected route component that redirects to login if not authenticated
const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const unsubscribe = observeAuthState((user, isAdmin) => {
      setIsAuthenticated(!!user && isAdmin);
    });

    return () => unsubscribe();
  }, []);

  if (isAuthenticated === null) {
    // Still checking authentication, show loading or nothing
    return <div className="flex justify-center items-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
    </div>;
  }

  return isAuthenticated ? children : <Navigate to="/admin/login" replace />;
};

function App() {
  // Apply the Leaflet icon fix when the app loads
  useEffect(() => {
    fixLeafletIcon();
  }, []);
  
  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/properties" element={<PropertiesPage />} />
        <Route path="/property/:id" element={<PropertyDetailPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        
        {/* Admin routes */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route 
          path="/admin/dashboard" 
          element={
            <ProtectedRoute>
              <AdminDashboardPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/property/new" 
          element={
            <ProtectedRoute>
              <PropertyEditorPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/property/edit/:id" 
          element={
            <ProtectedRoute>
              <PropertyEditorPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
